/**
 * context/AuthContext.js — localStorage demo version
 *
 * No JWT, no backend. Auth state lives entirely in localStorage:
 *   lifeos_user     → serialized user object (presence = "logged in")
 *   lifeos_session  → "1" flag so we can detect an active session across
 *                     page refreshes without re-reading the full object
 *
 * The API shape (user, login, register, logout, isLoading) is identical
 * to the full-stack version so all consuming components work unchanged.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount: restore session from localStorage (no network call needed)
  useEffect(() => {
    const stored = localStorage.getItem('lifeos_user');
    if (stored) {
      try { setUser(JSON.parse(stored)); } catch { /* corrupted — ignore */ }
    }
    setIsLoading(false);
  }, []);

  /** Login: verify credentials against localStorage via authAPI */
  const login = async (email, password) => {
    const data = await authAPI.login({ email, password });
    setUser(data.user);
    return data;
  };

  /** Register: create new user in localStorage via authAPI */
  const register = async (name, email, password) => {
    const data = await authAPI.register({ name, email, password });
    setUser(data.user);
    return data;
  };

  /** Logout: clear session flag but keep data keys so demo persists */
  const logout = () => {
    localStorage.removeItem('lifeos_user');
    localStorage.removeItem('lifeos_password');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
